/**
 * The exchange the home page demonstrates.
 *
 * Deliberately NOT in the locale files. Those hold one language at a time, and
 * this needs two at once: the visitor's side is written in the site's language,
 * the other side in whatever that language is most likely to be crossing. A
 * translation demo whose two halves are the same language demonstrates nothing.
 *
 * The pairing rule is the whole point of the exercise, so it is a function with
 * a test rather than a lookup table someone can quietly get wrong.
 */

import type { Locale } from '@/lib/i18n';

export interface DemoLines {
  /** You ask for something. */
  ask: string;
  /** They agree, with a condition. */
  agree: string;
  /** You propose the next step. */
  plan: string;
  /** They accept and offer to arrange it. */
  book: string;
}

/**
 * Who is on the other end.
 *
 * English readers are shown Japanese, because Sokuji is a Japanese word made in
 * Japan and that pairing says so without a sentence of copy. Everyone else is
 * shown English: it is the language they are most likely to be crossing into,
 * and — unlike a same-language pair — it makes the translation visible.
 */
export function counterpartLocale(locale: Locale): Locale {
  return locale === 'en' ? 'ja' : 'en';
}

export const DEMO_SCRIPT: Record<Locale, DemoLines> = {
  en: {
    ask: 'Could we bring the release forward to next Wednesday?',
    agree: 'Sure, if QA signs off by Monday.',
    plan: "Let's walk through the release steps on Wednesday.",
    book: "Works for me. I'll book a room.",
  },
  ja: {
    ask: '来週の水曜、リリースを前倒しできますか。',
    agree: '月曜までにQAが通れば大丈夫です。',
    plan: 'では、水曜にリリース手順を確認しましょう。',
    book: '大丈夫です。部屋を押さえておきます。',
  },
  zh: {
    ask: '下周三能把发布提前吗？',
    agree: '只要周一前通过 QA 就行。',
    plan: '那周三一起过一遍发布步骤吧。',
    book: '没问题，我去订个会议室。',
  },
  ko: {
    ask: '다음 주 수요일로 릴리스를 앞당길 수 있을까요?',
    agree: '월요일까지 QA만 통과하면 괜찮습니다.',
    plan: '그럼 수요일에 릴리스 절차를 함께 확인하죠.',
    book: '좋습니다. 회의실을 잡아 두겠습니다.',
  },
  es: {
    ask: '¿Podemos adelantar la publicación al próximo miércoles?',
    agree: 'Sí, si QA da el visto bueno antes del lunes.',
    plan: 'Entonces repasemos los pasos el miércoles.',
    book: 'Me viene bien. Reservo una sala.',
  },
  fr: {
    ask: 'Pouvons-nous avancer la publication à mercredi prochain ?',
    agree: 'Oui, si la QA valide avant lundi.',
    plan: 'Alors passons en revue les étapes mercredi.',
    book: 'Ça me va. Je réserve une salle.',
  },
  de: {
    ask: 'Können wir das Release auf nächsten Mittwoch vorziehen?',
    agree: 'Ja, wenn QA bis Montag freigibt.',
    plan: 'Dann gehen wir die Schritte am Mittwoch durch.',
    book: 'Passt. Ich reserviere einen Raum.',
  },
  it: {
    ask: 'Possiamo anticipare il rilascio a mercoledì prossimo?',
    agree: 'Sì, se il QA approva entro lunedì.',
    plan: 'Allora mercoledì rivediamo i passaggi.',
    book: 'Per me va bene. Prenoto una sala.',
  },
  pt: {
    ask: 'Podemos antecipar o lançamento para a próxima quarta?',
    agree: 'Sim, se o QA aprovar até segunda.',
    plan: 'Então vamos revisar os passos na quarta.',
    book: 'Por mim tudo bem. Reservo uma sala.',
  },
  ru: {
    ask: 'Можем перенести релиз на следующую среду?',
    agree: 'Да, если QA примет до понедельника.',
    plan: 'Тогда в среду пройдём по шагам релиза.',
    book: 'Мне подходит. Забронирую переговорную.',
  },
  uk: {
    ask: 'Можемо перенести реліз на наступну середу?',
    agree: 'Так, якщо QA підтвердить до понеділка.',
    plan: 'Тоді в середу пройдімося по кроках релізу.',
    book: 'Мені підходить. Заброную переговорну.',
  },
  ar: {
    ask: 'هل يمكننا تقديم الإصدار إلى الأربعاء القادم؟',
    agree: 'نعم، إذا اعتمد فريق الجودة قبل الاثنين.',
    plan: 'إذن لنراجع خطوات الإصدار يوم الأربعاء.',
    book: 'يناسبني. سأحجز قاعة.',
  },
};

/** The two-letter tag the app itself shows on a transcript line. */
export const LANGUAGE_TAG: Record<Locale, string> = {
  en: 'en',
  ja: 'ja',
  zh: 'zh',
  ko: 'ko',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
  pt: 'pt',
  ru: 'ru',
  uk: 'uk',
  ar: 'ar',
};

/**
 * What a language calls itself.
 *
 * The diagrams name the language rather than tagging it: a hop labelled
 * `ja` reads as metadata, and `日本語` reads as the thing being spoken. The
 * two-letter tags above stay where the app itself uses them — on a transcript
 * line and in the session's language pair.
 */
export const LANGUAGE_NAME: Record<Locale, string> = {
  en: 'English',
  ja: '日本語',
  zh: '中文',
  ko: '한국어',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  ru: 'Русский',
  uk: 'Українська',
  ar: 'العربية',
};

/**
 * Roughly how many characters a line has, for the typewriter reveal. Latin
 * scripts reveal per character; CJK carries far more meaning per glyph, so a
 * per-character reveal there would crawl. Steps are capped so no line takes
 * noticeably longer than its slot.
 */
export function revealSteps(text: string): number {
  return Math.min(text.length, 60);
}
